import React, { FC, useContext, useEffect, useState } from 'react';
import { DialogComponent } from 'ReusableComponents/Modal/ModalComponent';
import { Dayjs } from 'dayjs';
import { CarType, CourierJson } from 'types/bundleTypes';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import {
  createMultipleShifts,
  fetchCourierRequests,
  requestCourier,
} from '../shiftApi';
import {
  CourierFilterSection,
  CourierLists,
  CourierListSection,
  RoleFilterType,
} from './courierModalComponents';
import './CourierModals.scss';
import { toIsoDate } from 'utils/date';
import clsx from 'clsx';
import { handleError } from '../utils';
import { ShiftPlannerContext } from '../ShiftPlanner';
import { Region } from '../../../types/orderTypes';

// If shiftId is provided, the CourierPickerModal will only allow
// selection of a single courier (by disabling button)
const CourierPickerModal: FC<{
  shiftId?: string;
  date: Dayjs;
  wishList: number[];
  couriers: CourierJson[];
  showModal: boolean;
  setShowModal: (open: boolean) => void;
}> = ({ shiftId, date, wishList, couriers, showModal, setShowModal }) => {
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<
    Set<CarType>
  >(new Set());
  const [courierSearch, setCourierSearch] = useState<string>('');

  const [displayedCouriers, setDisplayedCouriers] = useState<CourierLists>({});
  const [selectedCouriers, setSelectedCouriers] = useState<Set<number>>(
    new Set()
  );
  const [checkedValue, setCheckedValue] = useState<RoleFilterType>(
    RoleFilterType.ALL
  );
  const [courierRequests, setCourierRequests] = useState<number[]>([]);
  const { region, couriersEfficiency } = useContext(ShiftPlannerContext);

  const handleAssignCouriers = () => {
    shiftId != null
      ? requestCourier(shiftId, [...selectedCouriers][0]).catch(handleError)
      : createMultipleShifts(date, [...selectedCouriers]).catch(handleError);
    setShowModal(false);
  };

  const hasSelectedCourier = selectedCouriers.size > 0;
  const isValidCourierSelection = shiftId ? selectedCouriers.size === 1 : true;
  const disableAssignmentButton = !(
    hasSelectedCourier && isValidCourierSelection
  );

  useEffect(() => {
    fetchCourierRequests(date, date, region)
      .then(requests => setCourierRequests(requests[toIsoDate(date)] ?? []))
      .catch(handleError);
  }, [date, region]);

  useEffect(() => {
    let filteredCouriers = couriers.filter(courier =>
      courier.fullName.toLowerCase().includes(courierSearch.toLowerCase())
    );

    if (selectedVehicleTypes.size > 0) {
      filteredCouriers = filteredCouriers.filter(courier =>
        selectedVehicleTypes.has(courier.carType)
      );
    }

    const Wish = courierRequests
      .map(courierId =>
        filteredCouriers.find(courier => courierId === courier.id)
      )
      .filter(courier => !!courier && courier.companyNumber);

    let All: CourierJson[];
    switch (checkedValue) {
      case RoleFilterType.COURIER:
        filteredCouriers = filteredCouriers.filter(
          courier => courier.isCourier
        );
        setDisplayedCouriers({
          Wish: Wish.filter(courier => courier.isCourier),
          Courier: filteredCouriers,
        });
        return;
      case RoleFilterType.SORTER:
        filteredCouriers = filteredCouriers.filter(courier => courier.isSorter);
        setDisplayedCouriers({
          Wish: Wish.filter(courier => courier.isSorter),
          Sorter: filteredCouriers,
        });
        return;
      case RoleFilterType.ALL:
        All = filteredCouriers.filter(courier => !Wish.includes(courier));
        setDisplayedCouriers({ Wish, All });
        return;
    }
  }, [
    couriers,
    wishList,
    courierSearch,
    selectedVehicleTypes,
    checkedValue,
    courierRequests,
  ]);

  return (
    <DialogComponent
      className="courierPickerModal"
      open={showModal}
      setOpen={setShowModal}
    >
      <div className="courierPickerModal__hero">
        <h1>Add courier/sorter</h1>
        <p>{date.format('DD.MM.YYYY')}</p>
      </div>
      <CourierFilterSection
        selectedVehicleTypes={selectedVehicleTypes}
        setSelectedVehicleTypes={setSelectedVehicleTypes}
        courierSearch={courierSearch}
        setCourierSearch={setCourierSearch}
        checkedValue={checkedValue}
        setCheckedValue={setCheckedValue}
      />
      <CourierListSection
        selectedCouriers={selectedCouriers}
        setSelectedCouriers={setSelectedCouriers}
        courierLists={displayedCouriers}
        courierSelectionLimit={shiftId ? 1 : null}
        couriersEfficiency={couriersEfficiency}
        showPerformanceMeasures
        allowAllCouriersSelection
      />
      {Object.keys(couriersEfficiency).length === 0 &&
        region === Region.OSLO && (
          <div className="courierPickerModal__info">
            * Couriers efficiency could not be fetched, using default value of 1
          </div>
        )}
      <div className="courierPickerModal__buttons">
        <ButtonComponent
          className={clsx(
            'courierPickerModal__buttons__confirm',
            'sharedButton'
          )}
          text={
            selectedCouriers.size > 0
              ? `Assign to shift (${selectedCouriers.size})`
              : 'Assign to shift'
          }
          disabled={disableAssignmentButton}
          onClick={handleAssignCouriers}
          customColor={{ background: '#443df6', color: 'white' }}
        />
        <ButtonComponent
          className={clsx(
            'courierPickerModal__buttons__cancel',
            'sharedButton'
          )}
          text="Cancel"
          onClick={() => setShowModal(false)}
        />
      </div>
    </DialogComponent>
  );
};

export default CourierPickerModal;
